import { PriceUtils } from '@wix/bookings-uou-mappers';
import { SelectedVariants } from '@wix/bookings-uou-types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  type PaymentOption,
  ReservedPaymentOptionIds,
} from '../../../../../../types/types';
import { DepositOrFullAmountOptionIds } from '../../../../../../utils/payment/paymentOptions/utils';
import { getPriceLineText } from '../../../../../../utils/payment';
import type { SlotService } from '../../../../../../utils/state/types';
import { TagName } from '../../../../../../utils/tagName.const';
import { useFormComponentContext } from '../../../../Hooks/useFormComponentContext';
import Text, { TextType } from '../../../../Text/Text';
import {
  DiscountRuleName,
  PriceBeforeDiscount,
} from '../../../Discount/Discount';
import { PaymentDetails } from '../../../PaymentDetails/PaymentDetails';
import { classes, st } from './PriceLine.st.css';

export type PriceLineProps = {
  dataHook: string;
  selectedPaymentOption: PaymentOption;
  slotService: SlotService;
  selectedVariant?: SelectedVariants;
  totalNumberOfParticipants?: number;
  isCart: boolean | undefined;
};

const PriceLine: React.FC<PriceLineProps> = ({
  dataHook,
  selectedPaymentOption,
  slotService,
  selectedVariant,
  totalNumberOfParticipants,
  isCart,
}) => {
  const { dateRegionalSettingsLocale } = useFormComponentContext();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const isDepositOption =
    selectedPaymentOption.id === DepositOrFullAmountOptionIds.Deposit;
  const isFullAmountOption =
    selectedPaymentOption.id === DepositOrFullAmountOptionIds.FullAmount;

  if (
    selectedPaymentOption.id === ReservedPaymentOptionIds.SingleSession ||
    isDepositOption ||
    isFullAmountOption
  ) {
    const priceText = getPriceLineText({
      t,
      dateRegionalSettingsLocale,
      slotService,
      selectedVariant,
      totalNumberOfParticipants,
      isFullAmountOption,
    });

    if (!priceText) {
      return null;
    }

    const { paymentDetails } = slotService;
    const { priceBeforeDiscount, currency, discountRuleName } = paymentDetails;
    const priceBeforeDiscountText = priceBeforeDiscount
      ? PriceUtils.getFormattedCurrency({
          price: priceBeforeDiscount,
          currency,
          locale: dateRegionalSettingsLocale,
        })
      : undefined;

    return (
      <div className={st(classes.root, { isMobile })} data-hook={dataHook}>
        <div className={classes.priceContainer}>
          <PriceBeforeDiscount
            price={priceBeforeDiscountText}
            type={TextType.Secondary}
          />
          <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
            {priceText}
          </Text>
        </div>
        <DiscountRuleName
          name={discountRuleName}
          isCart={isCart}
          align="start"
        />
      </div>
    );
  } else {
    return (
      <div className={st(classes.root, { isMobile })}>
        <PaymentDetails paymentOption={selectedPaymentOption} isCart={isCart} />
      </div>
    );
  }
};

export default PriceLine;
